import { Card, CardContent, Grid, LinearProgress } from "@mui/material";
import { useSupabaseLoader } from "../../server/supabase/hooks";
import { useAccount } from "../generic/hooks/use-account";
import { CreateMomentCard } from "../moment-card/components/create-moment-card";
import { MomentCardMemo } from "../moment-card/components/moment-card";
import { SupabaseProgramService } from "../programs/server/supabase-program-service";

import type { UUID } from "../../utils/uuid";
import type { UseMomentProperties } from "../moment-card/types";
import type { GridProps } from "@mui/material";
import type { FC, PropsWithChildren } from "react";

interface MomentGridProps extends GridProps {
  moments: UseMomentProperties[];
  programId?: UUID;
  showCreate?: boolean;
  showProgram?: boolean;
}

export const MomentGrid: FC<MomentGridProps & PropsWithChildren> = ({
  moments,
  programId,
  showCreate = true,
  showProgram = false,
  children,
  ...gridProps
}) => {
  const { data: account } = useAccount();

  const { data: program } = useSupabaseLoader(
    async ({ supabase }) => {
      if (programId) {
        const { data } = await new SupabaseProgramService(supabase).getPopulated(programId);
        return data;
      }
    },
    [programId],
  );

  if (programId && !program) return <LinearProgress />;

  return (
    <Grid
      container
      spacing={2}
      gridAutoRows="1fr"
      columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 6 }}
      data-testid="moment-grid"
      {...gridProps}
    >
      {moments.map((moment) => (
        <Grid key={moment.id} size={{ xs: 1 }} minWidth={300}>
          <MomentCardMemo
            account={account ?? undefined}
            draggable={true}
            moment={moment}
            showProgram={showProgram}
          />
        </Grid>
      ))}

      {moments.length === 0 && children && (
        <Grid size={{ xs: 1 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>{children}</CardContent>
          </Card>
        </Grid>
      )}
      {showCreate && program && (
        <Grid size={{ xs: 1 }}>
          <CreateMomentCard programColor={program.theme_colour} programId={programId} />
        </Grid>
      )}
    </Grid>
  );
};
